import { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/Layout';
import { PickCard } from '@/components/PickCard';
import { PickCardGated } from '@/components/PickCardGated';
import { ResultsWidget } from '@/components/ResultsWidget';
import { RecentWins } from '@/components/RecentWins';
import { TwitterFeed } from '@/components/TwitterFeed';
import { EmailCapture } from '@/components/EmailCapture';
import { SocialProof } from '@/components/SocialProof';
import { Button } from '@/components/ui/button';
import { Loader2, TrendingUp, ChevronRight, ChevronDown, Zap, DollarSign, Activity, Mail, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Affiliate Links
const PRIZEPICKS_URL = "https://prizepicks.onelink.me/FjtC/d9ee5h8t";
const NOVIG_URL = "https://novig.onelink.me/JHQQ/5wk3auau";
const BETR_URL = "https://engagebetr.onelink.me/auSX/STATLAB";

// Winible Email Capture URL
const WINIBLE_URL = "https://www.winible.com/checkout/1547324105701741163?pid=1547324105714324076";

export default function Home() {
  const [picks, setPicks] = useState([]);
  const [allPicks, setAllPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState('all');
  const [record, setRecord] = useState({ wins: 0, losses: 0, pushes: 0, winRate: 0 });

  const [seoData, setSeoData] = useState(null);

  const [tipping, setTipping] = useState(false);

  // Handle tip success from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tip_success')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleTip = async (tipType) => {
    setTipping(true);
    try {
      const res = await axios.post(`${API}/tip`, {
        tip_type: tipType,
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Tip error:', err);
    } finally {
      setTipping(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch SEO data for dynamic meta tags
        try {
          const seoResponse = await axios.get(`${API}/seo-data`);
          setSeoData(seoResponse.data);
          // Update document title dynamically
          if (seoResponse.data.title) {
            document.title = seoResponse.data.title;
          }
          // Update meta description dynamically
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc && seoResponse.data.description) {
            metaDesc.setAttribute('content', seoResponse.data.description);
          }
        } catch (seoError) {
          console.log('SEO data not available, using defaults');
        }

        // Fetch picks
        const picksResponse = await axios.get(`${API}/picks`);
        setAllPicks(picksResponse.data);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const todaysPicks = picksResponse.data.filter(pick => {
          const pickDate = new Date(pick.pick_date);
          pickDate.setHours(0, 0, 0, 0);
          return pickDate.getTime() === today.getTime();
        });
        
        setPicks(todaysPicks);

        // Fetch record summary
        const recordResponse = await axios.get(`${API}/results/summary`);
        const { wins, losses, pushes } = recordResponse.data;
        const total = wins + losses;
        const winRate = total > 0 ? ((wins / total) * 100).toFixed(2) : 0;
        setRecord({ wins, losses, pushes, winRate });
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const scrollToResearch = () => {
    document.getElementById('todays-research')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToResults = () => {
    document.getElementById('tracked-results')?.scrollIntoView({ behavior: 'smooth' });
  };

  const featuredWithVideo = allPicks.find(p => p.featured && p.thumbnail_url) 
    || allPicks.find(p => p.featured)
    || allPicks.find(p => p.thumbnail_url);
  
  const sports = ['all', ...new Set(picks.map(p => p.sport))];
  
  const filteredPicks = selectedSport === 'all' 
    ? picks.filter(p => !p.featured && p.id !== featuredWithVideo?.id)
    : picks.filter(p => p.sport === selectedSport && !p.featured && p.id !== featuredWithVideo?.id);

  // Safely get SEO title and description - ensure they are never empty strings
  const defaultTitle = "Free NBA Player Props Today | PrizePicks & Underdog Picks | PrimeStatLab";
  const defaultDescription = "Free NBA player props and picks updated daily. PrizePicks, Underdog Fantasy, and DraftKings prop analysis. Expert predictions for points, rebounds, assists props tonight.";
  
  const seoTitle = (seoData && typeof seoData.title === 'string' && seoData.title.trim()) 
    ? seoData.title 
    : defaultTitle;
  const seoDescription = (seoData && typeof seoData.description === 'string' && seoData.description.trim())
    ? seoData.description
    : defaultDescription;

  return (
    <Layout>
      <Helmet>
        <title>Free NBA Player Props Today | PrizePicks &amp; Underdog Picks | PrimeStatLab</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="NBA player props, PrizePicks picks today, Underdog Fantasy picks, free player props, NBA props tonight, best props today, player prop predictions, DFS picks, NBA betting analysis, props free, daily NBA picks" />
        <link rel="canonical" href="https://primestatlab.com" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Free NBA Player Props Today | PrimeStatLab" />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content="https://primestatlab.com" />
        <meta property="og:site_name" content="PrimeStatLab" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free NBA Player Props Today | PrimeStatLab" />
        <meta name="twitter:description" content={seoDescription} />
      </Helmet>
      
      <EmailCapture />
      <SocialProof />

      {/* ========== SECTION 1: HERO ========== */}
      <div className="bg-gradient-to-b from-card via-background to-background border-b border-border" data-testid="hero-banner">
        <div className="container mx-auto px-6 md:px-12 py-10 md:py-16">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-mono mb-4">
              <TrendingUp size={14} />
              <span>DAILY ANALYSIS</span>
            </div>
            
            {/* Headline - Updated copy */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4">
              DATA-DRIVEN PLAYER PROP <span className="text-primary animate-subtle-glow">ANALYSIS</span>
            </h1>
            
            {/* Subheadline - Updated copy */}
            <p className="text-lg md:text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
              Structured player prop research.<br className="hidden sm:block" />
              Published daily before 12 PM ET.
            </p>
            
            {/* Single Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-5">
              <Button
                onClick={scrollToResearch}
                className="bg-primary hover:bg-primary/90 text-white font-bold py-6 px-10 text-lg uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/20"
                data-testid="hero-cta-research"
              >
                See Today's Research
                <ChevronDown size={20} className="ml-2" />
              </Button>
              <Button
                onClick={scrollToResults}
                variant="outline"
                className="border-2 border-muted-foreground/30 hover:border-primary text-foreground font-bold py-6 px-10 text-lg uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                data-testid="hero-cta-results"
              >
                View Tracked Results
                <ChevronDown size={20} className="ml-2" />
              </Button>
            </div>

            {/* Minimal Authority Stat Strip - Hidden for non-subscribers */}
            <p className="text-xs text-muted-foreground/70 font-mono">
              Updated Daily Before 12 PM ET
            </p>
          </div>
        </div>
      </div>

      {/* ========== SECTION 2: FEATURED RESEARCH (VISIBLE IMMEDIATELY) ========== */}
      <div id="todays-research" className="container mx-auto px-6 md:px-12 py-10 md:py-14" data-testid="home-page">
        
        {/* Header - Increased size */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
          <div>
            <h2 className="text-[2.75rem] md:text-[3.25rem] font-heading font-bold mb-1">TODAY'S RESEARCH BOARD</h2>
            <p className="text-muted-foreground text-sm">Data-driven player prop analysis backed by stats and film study</p>
          </div>
          <div className="md:text-right">
            <ResultsWidget compact />
          </div>
        </div>

        {/* Featured Research Card - Simplified hierarchy */}
        {featuredWithVideo && (
          <div className="bg-card/80 border border-border border-l-[3px] border-l-green-500 p-6 mb-6 relative" data-testid="featured-research">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/[0.03] to-transparent pointer-events-none" />
            <div className="relative">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Content - Clear hierarchy */}
                <div className="flex flex-col justify-center">
                  {/* PRIMARY PLAY Label */}
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-green-500/70 mb-2">Primary Play</span>
                  
                  {/* Player Name - LARGEST */}
                  <h3 className="font-heading text-5xl md:text-6xl font-bold mb-2">{featuredWithVideo.player_name}</h3>
                  
                  {/* Prop + Line - SECOND */}
                  <p className="text-2xl text-primary font-mono mb-3">
                    {featuredWithVideo.prop_type}: {featuredWithVideo.pick} | Line {featuredWithVideo.line}
                  </p>
                  
                  {/* Trend Stat - THIRD */}
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {featuredWithVideo.reasoning}
                  </p>
                  
                  {/* Confidence */}
                  <div className="text-xs text-muted-foreground/70 font-mono mb-4">
                    CONFIDENCE: {featuredWithVideo.confidence_level}/10
                  </div>

                  <a 
                    href={`/pick/${featuredWithVideo.id}`}
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-heading text-sm uppercase tracking-widest transition-colors group"
                    data-testid="featured-full-analysis-link"
                  >
                    <span>Full Analysis</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </a>
                </div>
                
                {/* Right: Thumbnail */}
                <div className="aspect-video max-h-[280px] bg-surface rounded-lg overflow-hidden flex items-center justify-center">
                  {featuredWithVideo.thumbnail_url ? (
                    <img 
                      src={featuredWithVideo.thumbnail_url.startsWith('/') ? `${BACKEND_URL}${featuredWithVideo.thumbnail_url}` : featuredWithVideo.thumbnail_url} 
                      alt={`${featuredWithVideo.player_name} analysis`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center p-8">
                      <div className="text-7xl font-heading font-bold text-primary mb-2">
                        {featuredWithVideo.player_name?.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="text-sm text-muted-foreground font-mono uppercase">
                        {featuredWithVideo.prop_type}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== SECTION 3: EMAIL CAPTURE (UNLOCK FULL BREAKDOWN) ========== */}
        <div className="my-10" data-testid="email-capture-inline">
          <div className="bg-card border border-border border-l-[3px] border-l-primary p-6 md:p-8 max-w-3xl mx-auto">
            <div className="text-center">
              <h3 className="text-2xl md:text-3xl font-heading font-bold mb-2">
                GET FULL DAILY PLAYER PROP ANALYSIS
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                Published before 12:00 PM ET. Data-backed breakdowns. No hype.
              </p>
              {/* Friction reducer */}
              <p className="text-xs text-muted-foreground/50 mb-4 font-mono">
                Full breakdown unlocked instantly.
              </p>
              <a
                href={WINIBLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-4 px-8 text-base uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/20"
                data-testid="email-capture-cta"
              >
                <Mail size={18} />
                Unlock Today's Breakdown
              </a>
              <p className="text-xs text-muted-foreground/60 mt-4 font-mono">
                We publish structured player prop research daily. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </div>

        {/* ========== SECTION 4: ADDITIONAL RESEARCH TEASERS ========== */}
        {/* Scroll Flow Control - Divider */}
        {filteredPicks.length > 0 && (
          <div className="text-center my-8">
            <p className="text-xs text-muted-foreground/50 font-mono uppercase tracking-widest">
              ─── More Research Below ───
            </p>
          </div>
        )}

        {/* Sport Filter */}
        {sports.length > 2 && (
          <div className="flex gap-2 mb-6 flex-wrap">
            {sports.map(sport => (
              <button
                key={sport}
                onClick={() => setSelectedSport(sport)}
                className={`px-4 py-2 font-mono text-sm uppercase tracking-wider transition-colors ${
                  selectedSport === sport
                    ? 'bg-primary text-white'
                    : 'bg-card border border-border hover:border-primary'
                }`}
                data-testid={`filter-${sport}`}
              >
                {sport}
              </button>
            ))}
          </div>
        )}

        {/* Picks Grid - Gated */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-primary" size={40} />
          </div>
        ) : filteredPicks.length === 0 ? (
          <div className="text-center py-10 bg-card border border-border">
            <p className="text-muted-foreground mb-2">No additional analysis available for {selectedSport === 'all' ? 'today' : selectedSport} yet.</p>
            <p className="text-sm text-muted-foreground/70">Check back later or browse our recent research.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPicks.map((pick) => (
              <PickCardGated key={pick.id} pick={pick} />
            ))}
          </div>
        )}
      </div>

      {/* ========== SECTION 5: EXECUTION PLATFORMS ========== */}
      <section id="tracked-results" className="py-12 md:py-16 bg-background border-t border-border" data-testid="apps-section">
        <div className="container mx-auto px-6 md:px-12">
          {/* Section Header - Professional Tone */}
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">EXECUTION PLATFORMS</h2>
            <p className="text-muted-foreground/70 text-sm max-w-md mx-auto">
              Verified performance across platforms.
            </p>
          </div>

          {/* Three Lanes - Reduced visual weight */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            
            {/* Lane 1: PrizePicks */}
            <a
              href={PRIZEPICKS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-card/50 border border-border/50 rounded-lg p-5 transition-all duration-300 hover:border-green-500/30 hover:bg-card flex flex-col min-h-[180px]"
              data-testid="app-lane-prizepicks"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Zap size={16} className="text-green-500" />
                </div>
                <span className="text-xs font-mono uppercase tracking-wider text-green-500/80">Upside Plays</span>
              </div>
              <h3 className="text-lg font-heading font-bold mb-1">PrizePicks</h3>
              <p className="text-muted-foreground/70 text-xs mb-auto">Multiplier builds and ceiling projections.</p>
              <div className="flex items-center gap-1 text-green-500/70 font-mono text-xs uppercase tracking-wider group-hover:gap-2 transition-all mt-3">
                <span>View</span>
                <ChevronRight size={14} />
              </div>
            </a>

            {/* Lane 2: Novig */}
            <a
              href={NOVIG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-card/50 border border-border/50 rounded-lg p-5 transition-all duration-300 hover:border-blue-500/30 hover:bg-card flex flex-col min-h-[180px]"
              data-testid="app-lane-novig"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <DollarSign size={16} className="text-blue-500" />
                </div>
                <span className="text-xs font-mono uppercase tracking-wider text-blue-500/80">Best Price</span>
              </div>
              <h3 className="text-lg font-heading font-bold mb-1">Novig</h3>
              <p className="text-muted-foreground/70 text-xs mb-auto">Lower margin. Better long-term value.</p>
              <div className="flex items-center gap-1 text-blue-500/70 font-mono text-xs uppercase tracking-wider group-hover:gap-2 transition-all mt-3">
                <span>View</span>
                <ChevronRight size={14} />
              </div>
            </a>

            {/* Lane 3: Betr */}
            <a
              href={BETR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-card/50 border border-border/50 rounded-lg p-5 transition-all duration-300 hover:border-red-500/30 hover:bg-card flex flex-col min-h-[180px]"
              data-testid="app-lane-betr"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                  <Activity size={16} className="text-red-500" />
                </div>
                <span className="text-xs font-mono uppercase tracking-wider text-red-500/80">Live Tracking</span>
              </div>
              <h3 className="text-lg font-heading font-bold mb-1">Betr</h3>
              <p className="text-muted-foreground/70 text-xs mb-auto">Live volatility analysis.</p>
              <div className="flex items-center gap-1 text-red-500/70 font-mono text-xs uppercase tracking-wider group-hover:gap-2 transition-all mt-3">
                <span>View</span>
                <ChevronRight size={14} />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ========== SECTION 6: RESULTS / STREAK ========== */}
      <div className="container mx-auto px-6 md:px-12 py-10">
        <RecentWins />
      </div>

      {/* ========== SECTION 7: TIP JAR ========== */}
      <section className="container mx-auto px-6 md:px-12 py-8">
        <div className="bg-card border border-border rounded-lg p-6 text-center max-w-lg mx-auto" data-testid="tip-jar-section">
          <Heart className="mx-auto mb-3 text-primary" size={28} />
          <h3 className="font-heading text-xl font-bold mb-2">SUPPORT PRIMESTATLAB</h3>
          <p className="text-muted-foreground text-sm mb-5">
            All picks are free. Tips help keep the analysis coming.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              onClick={() => handleTip('coffee')}
              disabled={tipping}
              variant="outline"
              className="font-mono text-sm gap-1"
              data-testid="tip-coffee"
            >
              <DollarSign size={14} />5
            </Button>
            <Button
              onClick={() => handleTip('lunch')}
              disabled={tipping}
              variant="outline"
              className="font-mono text-sm gap-1"
              data-testid="tip-lunch"
            >
              <DollarSign size={14} />10
            </Button>
            <Button
              onClick={() => handleTip('mvp')}
              disabled={tipping}
              className="font-mono text-sm gap-1 bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="tip-mvp"
            >
              <DollarSign size={14} />25
            </Button>
          </div>
        </div>
      </section>

      {/* ========== SECTION 8: SOCIAL / FOOTER ========== */}
      <div className="container mx-auto px-6 md:px-12 pb-12">
        <TwitterFeed />
      </div>
    </Layout>
  );
}
